import { useCallback, useEffect, useState } from "react";
import useHorizontalSnapDrag, { SNAP_SIDE } from "hooks/useHorizontalSnapDrag";
import { LANGUAGE } from "services/general";
import { CHAT_LAUNCHER_HIDDEN_KEY, CHAT_LAUNCHER_SIDE_KEY } from "../constants";

const SIDES = Object.values(SNAP_SIDE);

// The launcher has always sat at the inline end: right in English, left in Arabic.
const defaultSide = () =>
  LANGUAGE === "ar" ? SNAP_SIDE.LEFT : SNAP_SIDE.RIGHT;

const readStoredSide = () => {
  try {
    const stored = localStorage.getItem(CHAT_LAUNCHER_SIDE_KEY);
    return SIDES.includes(stored) ? stored : null;
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage blocked (private mode, quota) — the in-memory state still holds.
  }
};

/**
 * State for the floating chat launcher: which side of the page it sits on
 * (dragged horizontally, persisted) and whether the user has hidden it
 * (persisted, but reset on every load so a reload always brings it back).
 */
export const useChatLauncher = () => {
  const [side, setSide] = useState(() => readStoredSide() ?? defaultSide());
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    writeStorage(CHAT_LAUNCHER_HIDDEN_KEY, null);
  }, []);

  const handleSnap = useCallback((nextSide) => {
    setSide(nextSide);
    writeStorage(CHAT_LAUNCHER_SIDE_KEY, nextSide);
  }, []);

  const hide = useCallback(() => {
    setIsHidden(true);
    writeStorage(CHAT_LAUNCHER_HIDDEN_KEY, "1");
  }, []);

  const drag = useHorizontalSnapDrag({ onSnap: handleSnap });

  // Physical side → the flex alignment that puts the launcher there.
  const isRtl = LANGUAGE === "ar";
  const isAtEnd = (side === SNAP_SIDE.RIGHT) !== isRtl;

  return { side, isAtEnd, isHidden, hide, ...drag };
};
